import { DataTracker, EventValue, FieldType } from './databinding/data-tracker';
import { EventSnippet, Snippet } from '../../interop/webmodule-interop';
import { html, TemplateResult } from 'lit';

import { createConfirmCancelButtons } from './modal-footer-buttons';
import { DataBinding } from './databinding/databinding';
import { LitBaseModal } from './modal/modal-factory-lit';

import { createRef, ref } from 'lit/directives/ref.js';
import { FormInputAssistant } from './templateresult/form-input-assistant';
import { LangModalConfirmation } from '../../../localization/webmodule-common/other/ui/modal-confirmation-lang';

// Interface for the dynamic form fields
export interface DynamicFormField {
  maxLength: number | undefined;
  label: string;
  value: EventValue;
  valueType?: FieldType;
  required?: boolean;
  placeholder?: string;
  readonly?: boolean;
  options?: Array<{ value: string; label: string }>;
}

// Main class for the dynamic input modal
export class DynamicInputModal extends LitBaseModal {
  eventTitle: EventSnippet;
  fields: DynamicFormField[];
  confirmLabel: string;
  cancelLabel: string;
  internalId: string;
  formRef = createRef<HTMLFormElement>();

  private binding: DataBinding;
  private dataTracker: DataTracker;
  public confirmed: boolean = false;

  constructor(
    title: EventSnippet,
    fields: DynamicFormField[],
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
    }
  ) {
    super();
    this.internalId = `dynamic-form-${Date.now()}`;
    this.binding = new DataBinding(() => this.formRef.value!, this.internalId);
    this.dataTracker = new DataTracker(this.binding);

    this.eventTitle = title;
    this.fields = fields;
    this.confirmLabel = options?.confirmLabel ?? LangModalConfirmation.saveAndClose();
    this.cancelLabel = options?.cancelLabel ?? LangModalConfirmation.cancel();

    this.fields.forEach((field, index) => {
      this.dataTracker.addDynamic(
        `field${index}`,
        field.valueType ?? FieldType.string,
        () => field.value,
        (value: EventValue) => {
          field.value = value;
        },
        { nullable: !field.required }
      );
    });
  }

  protected get modalClasses(): string {
    return 'modal-dialog';
  }

  protected get modalSize() {
    return 'modal-lg';
  }

  title(): Snippet {
    return this.eventTitle();
  }

  private getFieldTemplate(field: DynamicFormField, index: number, form: FormInputAssistant): TemplateResult {
    const fieldName = `field${index}`;

    // Choose the appropriate input type based on field.valueType
    switch (field.valueType) {
      case FieldType.int:
        return form.int(fieldName, field.label, {
          min: field.options?.find(o => o.value === 'min')?.label
            ? Number(field.options.find(o => o.value === 'min')?.label)
            : undefined,
          max: field.options?.find(o => o.value === 'max')?.label
            ? Number(field.options.find(o => o.value === 'max')?.label)
            : undefined,
          toolTip: field.placeholder
        });

      case FieldType.float:
        return form.float(fieldName, field.label, {
          min: field.options?.find(o => o.value === 'min')?.label
            ? Number(field.options.find(o => o.value === 'min')?.label)
            : undefined,
          max: field.options?.find(o => o.value === 'max')?.label
            ? Number(field.options.find(o => o.value === 'max')?.label)
            : undefined,
          toolTip: field.placeholder
        });

      case FieldType.money:
        return form.money(fieldName, field.label, {
          toolTip: field.placeholder
        });

      case FieldType.date:
        return form.date(fieldName, field.label, 'date', field.placeholder);

      case FieldType.dateTime:
        return form.date(fieldName, field.label, 'datetime-local', field.placeholder);

      case FieldType.boolean:
        return form.checkbox(fieldName, field.label, {
          toolTip: field.placeholder,
          readonly: field.readonly
        });

      case FieldType.string:
      default:
        // For string or any unspecified type, use text input
        if (field.required) {
          return form.textRequired(fieldName, field.label, field.maxLength);
        } else {
          return form.text1(fieldName, field.label, {
            maxLength: field.maxLength,
            placeholder: field.placeholder,
            toolTip: field.placeholder
          });
        }
    }
  }

  public bodyTemplate() {
    const form = new FormInputAssistant(this.dataTracker, false, false);
    return html`
      <form ${ref(this.formRef)} id="${this.internalId}" class="p-2">
        ${this.fields.map((field, index) => this.getFieldTemplate(field, index, form))}
      </form>
    `;
  }

  public footerTemplate(): TemplateResult | null {
    const confirmEvent = () => {
      if (this.validateForm()) {
        this.dataTracker.applyChangeToValue();
        this.confirmed = true;
        this.hideModal();
      }
    };

    return createConfirmCancelButtons(this.confirmLabel, confirmEvent, this.cancelLabel, () => this.hideModal());
  }

  private validateForm(): boolean {
    // If we have a form element, check its validity
    if (this.formRef.value) {
      return this.formRef.value.reportValidity();
    }
    return true;
  }

  protected ZIndex(): number | undefined {
    return 99993;
  }
}

// Helper function to show a dynamic input modal
export async function showDynamicInputModal(
  title: EventSnippet,
  fields: DynamicFormField[],
  options?: {
    confirmLabel?: string;
    cancelLabel?: string;
  }
): Promise<{ confirmed: boolean; fields: DynamicFormField[] }> {
  const modal = new DynamicInputModal(title, fields, options);
  await modal.showModal();
  return {
    confirmed: modal.confirmed,
    fields: modal.fields
  };
}
